import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Megaphone, Plus, Save, Trash2, X, Eye, EyeOff,
  Info, AlertTriangle, AlertOctagon, CheckCircle2,
} from "lucide-react";

const SEVERITIES = [
  { key: "info", label: "Info", icon: Info, cls: "text-sky-700 bg-sky-50 border-sky-300" },
  { key: "warning", label: "Aviso", icon: AlertTriangle, cls: "text-amber-700 bg-amber-50 border-amber-300" },
  { key: "critical", label: "Crítico", icon: AlertOctagon, cls: "text-red-700 bg-red-50 border-red-300" },
  { key: "success", label: "Sucesso", icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50 border-emerald-300" },
];

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (_e) { return iso; }
};

// Convert an ISO string into HTML datetime-local input value ("YYYY-MM-DDTHH:mm").
const toLocalInputValue = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromLocalInputValue = (v) => (v ? new Date(v).toISOString() : null);

function AnnouncementForm({ initial, onCancel, onSaved }) {
  const isEdit = !!initial?.id;
  const [f, setF] = useState({
    title: initial?.title || "",
    message: initial?.message || "",
    severity: initial?.severity || "info",
    starts_at: toLocalInputValue(initial?.starts_at),
    ends_at: toLocalInputValue(initial?.ends_at),
    dismissible: initial?.dismissible !== false,
    active: initial?.active !== false,
  });
  const [busy, setBusy] = useState(false);

  // Quick presets for duration
  const presetHours = (hours) => {
    const now = new Date();
    const end = new Date(now.getTime() + hours * 3600 * 1000);
    setF((s) => ({ ...s, ends_at: toLocalInputValue(end.toISOString()) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (f.title.trim().length < 2) return toast.error("Título muito curto");
    if (f.message.trim().length < 2) return toast.error("Mensagem muito curta");
    setBusy(true);
    try {
      const payload = {
        title: f.title.trim(),
        message: f.message.trim(),
        severity: f.severity,
        starts_at: fromLocalInputValue(f.starts_at),
        ends_at: fromLocalInputValue(f.ends_at),
        dismissible: f.dismissible,
        active: f.active,
      };
      if (isEdit) {
        await api.patch(`/admin/announcements/${initial.id}`, payload);
        toast.success("Aviso atualizado");
      } else {
        await api.post("/admin/announcements", payload);
        toast.success("Aviso criado — já visível para todos os usuários");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="border-2 border-[#002EA6] bg-white p-5 space-y-4" data-testid="announcement-form">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
          {isEdit ? "Editar aviso" : "Novo aviso"}
        </div>
        <button type="button" onClick={onCancel} className="text-xs text-slate-500 hover:text-slate-900" data-testid="announcement-form-cancel">
          Cancelar
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block md:col-span-2">
          <span className="block text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1">Título</span>
          <input
            data-testid="announcement-title"
            value={f.title}
            onChange={(e) => setF({ ...f, title: e.target.value })}
            maxLength={140}
            placeholder="Ex: Manutenção programada — sábado 22h"
            className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#002EA6]"
          />
        </label>

        <label className="block md:col-span-2">
          <span className="block text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1">Mensagem</span>
          <textarea
            data-testid="announcement-message"
            value={f.message}
            onChange={(e) => setF({ ...f, message: e.target.value })}
            maxLength={4000}
            rows={4}
            placeholder="O que os usuários precisam saber?"
            className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#002EA6] resize-y"
          />
        </label>

        <div className="md:col-span-2">
          <span className="block text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1">Severidade</span>
          <div className="flex flex-wrap gap-2">
            {SEVERITIES.map(({ key, label, icon: Icon, cls }) => (
              <button
                key={key}
                type="button"
                onClick={() => setF({ ...f, severity: key })}
                data-testid={`sev-${key}`}
                className={`px-3 py-1.5 text-xs font-bold border-2 ${cls} ${f.severity === key ? "ring-2 ring-offset-1 ring-[#002EA6]" : "opacity-70 hover:opacity-100"}`}
              >
                <Icon className="w-3.5 h-3.5 inline mr-1" /> {label}
              </button>
            ))}
          </div>
        </div>

        <label className="block">
          <span className="block text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1">Início (opcional)</span>
          <input
            type="datetime-local"
            data-testid="announcement-starts-at"
            value={f.starts_at}
            onChange={(e) => setF({ ...f, starts_at: e.target.value })}
            className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#002EA6]"
          />
          <span className="text-[11px] text-slate-500 mt-0.5 block">Vazio = já ativo agora</span>
        </label>

        <label className="block">
          <span className="block text-[10px] uppercase tracking-widest font-bold text-slate-600 mb-1">Fim / Expiração</span>
          <input
            type="datetime-local"
            data-testid="announcement-ends-at"
            value={f.ends_at}
            onChange={(e) => setF({ ...f, ends_at: e.target.value })}
            className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#002EA6]"
          />
          <div className="text-[11px] mt-1 flex flex-wrap gap-1">
            <button type="button" onClick={() => presetHours(1)} className="px-2 py-0.5 border border-slate-300 hover:border-slate-900 text-slate-600">+1h</button>
            <button type="button" onClick={() => presetHours(6)} className="px-2 py-0.5 border border-slate-300 hover:border-slate-900 text-slate-600">+6h</button>
            <button type="button" onClick={() => presetHours(24)} className="px-2 py-0.5 border border-slate-300 hover:border-slate-900 text-slate-600">+1d</button>
            <button type="button" onClick={() => presetHours(24 * 7)} className="px-2 py-0.5 border border-slate-300 hover:border-slate-900 text-slate-600">+1sem</button>
            <button type="button" onClick={() => presetHours(24 * 30)} className="px-2 py-0.5 border border-slate-300 hover:border-slate-900 text-slate-600">+1mês</button>
            <button type="button" onClick={() => setF({ ...f, ends_at: "" })} className="px-2 py-0.5 border border-slate-300 hover:border-slate-900 text-slate-600">Sem fim</button>
          </div>
        </label>

        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            data-testid="announcement-dismissible"
            checked={f.dismissible}
            onChange={(e) => setF({ ...f, dismissible: e.target.checked })}
          />
          <span>Usuário pode fechar (reaparece na próxima sessão)</span>
        </label>
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            data-testid="announcement-active"
            checked={f.active}
            onChange={(e) => setF({ ...f, active: e.target.checked })}
          />
          <span>Ativo</span>
        </label>
      </div>

      <div className="flex justify-end pt-2 border-t border-slate-100">
        <button
          type="submit"
          data-testid="announcement-save"
          disabled={busy}
          className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-[#002EA6] hover:bg-[#00207A] text-white flex items-center gap-2 disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {isEdit ? "Salvar alterações" : "Publicar aviso"}
        </button>
      </div>
    </form>
  );
}

export default function AdminAnnouncements() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [form, setForm] = useState(null); // null | {} for new | item for edit

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/announcements");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (item) => {
    try {
      await api.patch(`/admin/announcements/${item.id}`, { active: !item.active });
      load();
    } catch (_e) { toast.error("Falha"); }
  };
  const remove = async (item) => {
    if (!window.confirm(`Excluir "${item.title}"?`)) return;
    try {
      await api.delete(`/admin/announcements/${item.id}`);
      toast.success("Removido");
      load();
    } catch (_e) { toast.error("Falha"); }
  };

  const activeCount = items.filter((a) => a.active).length;

  return (
    <div className="space-y-4" data-testid="admin-announcements-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Admin</div>
          <h1 className="text-3xl font-black tracking-tighter flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-[#002EA6]" /> Avisos aos usuários
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Mensagens fixadas no topo de todas as telas. Usuários podem fechar,
            mas voltam a aparecer quando abrirem o sistema novamente. Ideal para
            avisos de manutenção, atualização ou instabilidade.
          </p>
        </div>
        {!form && (
          <button
            type="button"
            data-testid="announcement-new"
            onClick={() => setForm({})}
            className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-[#002EA6] hover:bg-[#00207A] text-white flex items-center gap-2"
          >
            <Plus className="w-3.5 h-3.5" /> Novo aviso
          </button>
        )}
      </div>

      {form && (
        <AnnouncementForm
          initial={form.id ? form : null}
          onCancel={() => setForm(null)}
          onSaved={() => { setForm(null); load(); }}
        />
      )}

      <div className="border border-slate-200 bg-white">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            Todos os avisos ({items.length}) · {activeCount} ativos
          </div>
        </div>
        {busy && !items.length ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="admin-announcements-empty">
            <Megaphone className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            Nenhum aviso criado ainda.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((a) => {
              const sev = SEVERITIES.find((s) => s.key === a.severity) || SEVERITIES[0];
              const SevIcon = sev.icon;
              return (
                <div key={a.id} className="px-4 py-3 flex flex-wrap items-start justify-between gap-3" data-testid={`admin-announcement-${a.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase border-2 ${sev.cls}`}>
                        <SevIcon className="w-3 h-3" /> {sev.label}
                      </span>
                      {!a.active && (
                        <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase bg-slate-100 text-slate-500 border border-slate-200">
                          desativado
                        </span>
                      )}
                      <span className="text-[10px] text-slate-500 font-mono">
                        criado {formatDate(a.created_at)} por {a.created_by_email}
                      </span>
                    </div>
                    <div className="mt-1.5 font-bold text-sm text-slate-900">{a.title}</div>
                    <div className="text-xs text-slate-600 mt-0.5 whitespace-pre-wrap">{a.message}</div>
                    <div className="text-[11px] text-slate-500 font-mono mt-1.5 flex flex-wrap gap-x-3">
                      <span>Início: {formatDate(a.starts_at) || "agora"}</span>
                      <span>Fim: {a.ends_at ? formatDate(a.ends_at) : "sem expiração"}</span>
                      <span>{a.dismissible ? "usuário pode fechar" : "não pode ser fechado"}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      data-testid={`announcement-toggle-${a.id}`}
                      onClick={() => toggleActive(a)}
                      className={`px-2 py-1.5 text-[11px] font-bold uppercase tracking-wider border ${a.active ? "border-slate-300 hover:border-slate-900 text-slate-700" : "border-emerald-500 text-emerald-700 hover:bg-emerald-50"}`}
                      title={a.active ? "Desativar" : "Ativar"}
                    >
                      {a.active ? <><EyeOff className="w-3 h-3 inline mr-1" /> Pausar</> : <><Eye className="w-3 h-3 inline mr-1" /> Ativar</>}
                    </button>
                    <button
                      type="button"
                      data-testid={`announcement-edit-${a.id}`}
                      onClick={() => setForm(a)}
                      className="px-2 py-1.5 text-[11px] font-bold uppercase tracking-wider border border-slate-300 hover:border-slate-900 text-slate-700"
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      data-testid={`announcement-delete-${a.id}`}
                      onClick={() => remove(a)}
                      className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50"
                      title="Excluir"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
